import {
  addSeconds,
  eachDayOfInterval,
  format,
  getDay,
  isToday,
  isYesterday,
  startOfDay,
} from "date-fns";

import { IDays, shiftWorkHour } from "@/interfaces/interfaces";
import { IBreakdown, IBreakdownData } from "@/interfaces/payroll.interface";
import {
  BreakDown,
  Holiday,
  IBreak,
  TimeShift,
} from "@/interfaces/timeTracking";

type GetMainBreakDownProps = {
  startTime: number;
  endTime: number;
  shifts: TimeShift[];
  day: IDays | string;
  holiday?: Holiday;
};

type GetMainBreakDownReturn = {
  id: string;
  time: number;
  day: string;
  title: string;
  start: number;
  end: number;
};

/**
 * @description it converts seconds to date
 * @param {number} sec - seconds
 * @returns {Date} - date
 */
export const secToDate = (sec: number): Date => {
  const date = new Date();
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  date.setHours(hours, minutes, 0, 0);

  return date;
};

/**
 * @description it converts seconds to date in 1H:30M format
 * @param {number} sec - seconds
 * @returns {string} - date in 1H:30M format
 */
export const secToDateFormat = (sec: number): string => {
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);

  return `${hours} H : ${minutes} M`;
};

/**
 * @description convert the seconed to AMPM
 * @param sec {number} -seconed
 * @returns {string} return time format
 */

export const seconedToHourMin = (sec: number): string => {
  if (sec === 0) {
    return "12:00 AM";
  }
  if (!sec) {
    return "0 H : 0 M";
  }
  const startofDay = startOfDay(new Date());
  const timeofDay = addSeconds(startofDay, sec);
  const ampm = format(timeofDay, "hh:mm a");

  return `${ampm}`;
};

/**
 * @description Converts a date to seconds since the start of the day.
 * @param {Date} date - The date to convert.
 * @returns {number} - The number of seconds since the start of the day.
 */
export const dateToSec = (date: Date | undefined): number => {
  const seconds =
    date?.getHours() === 0
      ? 0 + (date?.getMinutes() ?? 0) * 60
      : (date?.getHours() ?? 0) * 3600 + (date?.getMinutes() ?? 0) * 60;

  return seconds;
};

// donot change this order
export const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * @description it sorts the shifts
 * @param {TimeShift[]} shifts - list of shifts
 * @returns {TimeShift[]} - sorted shifts
 */
export function sortShifts(shifts: TimeShift[]): TimeShift[][] {
  return Array(shifts)?.sort((a: TimeShift[], b: TimeShift[]) => {
    const titles = ["Normal Hours", "Overnight Hours", "Weekend"];
    const indexA = titles.indexOf(a[0].title!);
    const indexB = titles.indexOf(b[0].title!);
    if (indexA < indexB) {
      return -1;
    }
    if (indexA > indexB) {
      return 1;
    }

    return 0;
  });
}

/**
 * @description it gives the next day
 * @param {string} currentDay - current day
 * @returns {string} - next day
 */
function nextDay(currentDay: string): string {
  // Find the index of the current day
  const currentIndex = dayNames.indexOf(currentDay);

  // Calculate the index of the next day
  const nextIndex = (currentIndex + 1) % 7;

  // Return the name of the next day
  return dayNames[nextIndex];
}

/**
 * @description it gives the starttime endtime and time that is spent on each shift
 * @param {GetMainBreakDownProps} object - get main breakdown props
 * @returns {GetMainBreakDownReturn[]} - list of breakdowns
 */
export function getBreakDown({
  startTime,
  endTime,
  shifts,
  day,
  holiday,
}: GetMainBreakDownProps): GetMainBreakDownReturn[] {
  let refinedShifts = [];

  for (let i = 0; i < shifts?.length; i++) {
    const s = shifts[i];

    if (s.startTime <= s.endTime) {
      refinedShifts.push(s);
    } else {
      // if startTime is greater than the end time
      // then it means that the shift extends to the next day
      // so we split it into two shifts then merge the breakdowns
      refinedShifts.push({ ...s, startTime: s.startTime, endTime: 86400 });
      refinedShifts.push({ ...s, startTime: 0, endTime: s.endTime });
    }
  }

  refinedShifts = refinedShifts.sort((a, b) =>
    a.rateConversion < b.rateConversion ? 1 : -1,
  );

  const breakDowns: {
    id: string;
    time: number;
    day: string;
    title: string;
    start: number;
    end: number;
  }[] = [];

  const isHoliday =
    holiday &&
    dayNames[getDay(holiday.date)].toUpperCase() === day.toUpperCase();

  const overnightShift = refinedShifts.find(
    (shift) => shift.title === "Overnight Hours",
  )!;
  // if it is holiday then just return the shift as it is
  if (isHoliday && startTime < endTime && endTime <= 86400) {
    breakDowns.push({
      id: overnightShift.id,
      start: startTime / 3600,
      end: endTime / 3600,
      time: endTime - startTime,
      title: "Overnight Hours",
      day,
    });

    return breakDowns;
  }

  for (let i = 0; i < refinedShifts.length; i++) {
    const currentInterval = refinedShifts[i];

    // if given start and end time falls inside the refined shifts
    if (
      startTime < currentInterval.endTime &&
      endTime > currentInterval.startTime &&
      currentInterval?.days.includes(day?.toUpperCase())
    ) {
      const startTimeInInterval = Math.max(
        currentInterval.startTime,
        startTime,
      );
      const endTimeInInterval = Math.min(currentInterval.endTime, endTime);

      breakDowns.push({
        id: currentInterval.id,
        start: startTimeInInterval / 3600,
        end: endTimeInInterval / 3600,
        time: endTimeInInterval - startTimeInInterval,
        title: currentInterval.title || "",
        day,
      });
    }
  }

  return breakDowns;
}

/**
 * @description it gives the starttime endtime and time that is spent on each shift
 * @param {GetMainBreakDownProps} object - get main breakdown props
 * @returns {GetMainBreakDownReturn[]} - list of breakdowns
 */
export function getMainBreakDown({
  startTime,
  endTime,
  shifts,
  day,
  holiday,
}: GetMainBreakDownProps): GetMainBreakDownReturn[] {
  let result = [];
  if (startTime <= endTime) {
    result = getBreakDown({
      shifts,
      startTime,
      endTime,
      day,
      holiday,
    });
  } else {
    // if startTime is greater than the end time
    // then it means that the shift extends to the next day
    // so we split it into two shifts then merge the breakdowns
    // let breakDown1;
    const breakDown1 = getBreakDown({
      startTime,
      endTime: 86400,
      day,
      shifts,
      holiday,
    });

    const breakDown2 = getBreakDown({
      startTime: 0,
      endTime,
      shifts,
      day: nextDay(day),
      holiday,
    });

    result = [...breakDown1, ...breakDown2];
  }

  return result;
}

/**
 * @description It calculates the allowed break time based on the start and end time.
 * @param {number} startTime - The start time in seconds.
 * @param {number} endTime - The end time in seconds.
 * @param {IBreak[]} breakList - The list of breaks.
 * @returns {number} The allowed break time in seconds.
 */
export function calculateAllowedBreak(
  startTime: number,
  endTime: number,
  breakList: IBreak[],
): number {
  let breaks = [];
  for (let i = 0; i < breakList?.length; i++) {
    const b = breakList[i];
    breaks.push(b);
  }
  breaks = breaks.sort((a, b) => (a.minTime < b.minTime ? 1 : -1));

  let totalTime;

  if (startTime < endTime) {
    totalTime = (endTime - startTime) / 60;
  } else if (startTime === endTime) {
    totalTime = 0;
  } else {
    totalTime = (86400 - startTime + endTime) / 60;
  }

  const allowedBreak = breaks.find((b) => {
    if (b.minTime <= totalTime && totalTime <= b.maxTime) {
      return b;
    }

    return undefined;
  });

  return allowedBreak?.allowed ?? 0;
}

/**
 * @description it gives the total hours between start and end time
 * @param {Date} startTime - start time
 * @param {Date} endTime - end time
 * @returns {number} - total hours
 */
export const calculateTotalUserHours = (
  startTime: Date,
  endTime: Date,
): number => {
  const start = dateToSec(startTime);
  const end = dateToSec(endTime);

  if (start < end) {
    return (end - start) / 3600;
  } else if (start === end) {
    return 0;
  } else {
    return (86400 - start + end) / 3600;
  }
};

export const toUTC = (date: Date | string): string | Date => {
  if (typeof date === "string") {
    return date;
  }

  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ),
  );
};

export const getLastMonday = (date: Date): Date => {
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);

  return new Date(date.setDate(diff));
};

/**
 * @param {Date} startDate - The start date
 * @param {Date} endDate - The end date
 * @returns {Date[]} - The range of dates between the start and end date
 * @description it takes start and end date and return range of dates between them
 */
export function getDateRange(
  startDate: Date | string,
  endDate: Date | string,
): Date[] {
  return eachDayOfInterval({
    start: startDate,
    end: endDate,
  });
}

export const getTotalHours = (
  data: shiftWorkHour[],
  shiftTitle: string,
): string => {
  const item = data?.filter((item) => item?.shiftTitle === shiftTitle);

  return secToDateFormat(item[0]?.time || 0);
};

export const timeRounder = (time: number): number => {
  const hours = time / 3600;

  // const doubledHours = hours * 2;
  // const rounded = Math.round(doubledHours);
  // const roundedHours = rounded / 2;

  // return Number(roundedHours.toFixed(2));

  return Number(hours.toFixed(2));
};

export const getShiftTime = (
  breakdown: BreakDown[],
  shift:
    | "Weekend"
    | "Overnight"
    | "Normal"
    | "Normal Hours"
    | "Overnight Hours",
): string => {
  const breaks = Object.values(breakdown).find((down) => down.name === shift);
  const breakTime =
    breaks?.time === 0 ? "-" : secToDateFormat(breaks?.time ?? 0);

  return breakTime;
};

export const getTime = (
  breakdown: IBreakdown,
  type: "Normal Hours" | "Overnight Hours" | "Weekend",
  dashIfZero?: boolean,
): string => {
  const item = Object.entries(breakdown)
    .find(([_, v]) => v.name === type)
    ?.filter((item) => typeof item !== "string") as unknown as IBreakdownData[];

  if (dashIfZero && item?.at(0)?.time === 0) {
    return "-";
  }

  return secToDateFormat(item?.at(0)?.time || 0);
};

export const dateFormat = (
  date: string | Date | undefined,
  Dateformat?: string,
): string => {
  try {
    if (!date) {
      return "-";
    }

    return format(date, Dateformat || "MM/dd/yyyy");
  } catch (error) {
    return "-";
  }
};

//Function to convert minutes to hours
export const dataToHours = (data: number): string => {
  return (data / 60).toFixed(0);
};

/**
 * This function converts days to milliseconds
 * @param {number} days - The number of days to convert to milliseconds.
 * @returns {number} The converted value.
 */
export const dayToMilliseconds = (days: number): number => {
  return days * 24 * 60 * 60 * 1000;
};

/**
 * This function converts milliseconds to days
 * @param {number} milliseconds - The number of milliseconds to convert to days.
 * @returns {number} The converted value.
 */
export const millisecondsToDay = (milliseconds: number): number => {
  return milliseconds / (24 * 60 * 60 * 1000);
};

/**
 * Combines a date with the current time
 * @param {Date} dateOnly - A date object (typically from a date picker)
 * @returns {Date} A new date object with the input date combined with the current time
 */
export const combineDateWithCurrentTime = (
  dateOnly: string | Date,
): Date | null => {
  if (!dateOnly) {
    return null;
  }
  // Convert string to Date object if it's not already
  const dateObj = typeof dateOnly === "string" ? new Date(dateOnly) : dateOnly;

  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return null;
  }

  const now = new Date();

  return new Date(
    dateObj.getFullYear(),
    dateObj.getMonth(),
    dateObj.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
    now.getMilliseconds(),
  );
};

export const timeToSeconds = (isoTimestamp: string): number => {
  const date = new Date(isoTimestamp);
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();

  return hours * 3600 + minutes * 60 + seconds;
};

export const dateToIso = (data: Date | string | number): string => {
  const newDate = new Date(data);

  return newDate.toISOString();
};

// helper for consistent formatting in date-fns
const DATE_FORMAT = "MMM d, yyyy";
export const DATE_TIME_FORMAT = "MMM do yyyy, h:mm a"; //Apr 28th 2025, 12:00 AM
export const DATE_TIME_DIGIT_FORMAT = "MM/dd/yyyy, h:mm a"; //04/28/2025, 12:00 AM
export const TIME_ONLY_FORMAT = "h:mm a"; //12:00 AM
export const DAY_MONTH_YEAR_FORMAT = "dd MMM, yyyy"; //28 Apr, 2025
export const MONTH_DAY_YEAR_FORMAT = "MMM dd, yyyy"; //Apr 28, 2025
export const YEAR_MONTH_DAY_FORMAT = "yyyy/MM/dd"; //2025/04/28
export const YEAR_MONTH_DAY_FORMAT2 = "yyyy-MM-dd"; //2025-04-28

/**
 * Returns a human-readable relative time string from the given date to now.
 * @param {string} dateString - The date string to be converted into a relative time.
 * @returns {string} A string representing the relative time from now.
 */
export function getRelativeTimeFromNow(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();

  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // Real-time difference calculation instead of static "Just now"
  if (seconds < 30) {
    return "Just now";
  }
  if (seconds < 60) {
    return `${seconds} seconds ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${format(date, TIME_ONLY_FORMAT)}`;
  }

  return `${format(date, DATE_FORMAT)} at ${format(date, TIME_ONLY_FORMAT)}`;
}

export const todayYesterDay = (date: string): string => {
  const parsedDate = new Date(date);

  if (isToday(parsedDate)) {
    return "Today";
  } else if (isYesterday(parsedDate)) {
    return "Yesterday";
  } else {
    return dateFormat(parsedDate, YEAR_MONTH_DAY_FORMAT);
  }
};
